import React, { FC, useCallback, useState } from 'react'
import { isEqual } from 'lodash'
import { ISortOrderEnum } from 'types/graphqlTypes'
import { ArrayParam, BooleanParam, StringParam } from 'use-query-params'
import PageList from 'apps/marketplace/components/PageList'
import { getSortingOptions } from 'apps/marketplace/utils/helpers/trailers'
import CarrierTrailerFilters from './CarrierTrailerFilters'
import CarrierTrailerFiltersForm from './CarrierTrailerFiltersForm'
import CarrierTrailerHeader from './CarrierTrailerHeader'
import CarrierTrailerRow from './CarrierTrailerRow'
import { useTrailerManagementContext } from './TrailerManagementContext'
import { ICarrierTrailerFragment } from './graphql/CarrierTrailerFragment'
import { useCarrierTrailers } from './hooks/useCarrierTrailers'
import { TCarrierTrailersFilters } from './types'

export const configQueryConfig = {
  shipmentIds: StringParam,
  trailerNumbers: StringParam,
  shipperIds: ArrayParam,
  facilities: ArrayParam,
  trailerStates: ArrayParam,
  pickupDate: StringParam,
  deliveryDate: StringParam,
  shipmentStates: ArrayParam,
  loadTypes: ArrayParam,
  showMissingTrailers: BooleanParam,
}

const DEFAULT_FILTER_VALUES: TCarrierTrailersFilters = {
  shipmentIds: '',
  trailerNumbers: '',
  shipperIds: [] as string[],
  facilities: [] as string[],
  pickupDate: null,
  deliveryDate: null,
  trailerStates: ['assigned_to_facility', 'assigned_to_shipment', 'empty_at_delivery', 'inactive'],
  sortOrder: ISortOrderEnum.DESC,
  sortBy: 'updated_at',
  shipmentStates: [],
  loadTypes: ['drop', 'live'],
  showMissingTrailers: false,
}

interface ICarrierTrailersProps {
  carrierId: string
}

const CarrierTrailers: FC<ICarrierTrailersProps> = ({ carrierId }) => {
  const { currentFilters, itemsPerPage, setCurrentFilters } = useTrailerManagementContext()
  const { showMissingTrailers = false } = currentFilters

  const { data, error, loading, pageInfo, totalCount } = useCarrierTrailers(
    currentFilters,
    carrierId
  )
  const { endCursor, startCursor } = pageInfo || {}
  const options = (data || []) as ICarrierTrailerFragment[]

  const [showFiltersPanel, setShowFiltersPanel] = useState(false)

  const pageChangeHandler = useCallback(
    (isNext, props) => {
      if (isNext) {
        setCurrentFilters({
          ...props,
          after: endCursor,
          before: undefined,
          first: itemsPerPage,
          last: undefined,
        })
      } else {
        setCurrentFilters({
          ...props,
          after: undefined,
          before: startCursor,
          first: undefined,
          last: itemsPerPage,
        })
      }
    },
    [endCursor, startCursor, setCurrentFilters, itemsPerPage]
  )

  const filterChangeHandler = useCallback(
    props => {
      if (isEqual(props, currentFilters)) return

      setCurrentFilters({
        ...props,
        before: null,
        after: null,
        first: itemsPerPage,
        last: undefined,
      })
    },
    [currentFilters, setCurrentFilters, itemsPerPage]
  )

  const sortOptions = getSortingOptions(showMissingTrailers, 'carrier')

  return (
    <div>
      <PageList
        Header={({ filters, setValues }) => (
          <CarrierTrailerFilters
            shipperIds={filters.shipperIds || []}
            facilities={filters.facilities || []}
            trailerStates={filters.trailerStates || []}
            showMissingTrailers={
              filters.showMissingTrailers || DEFAULT_FILTER_VALUES.showMissingTrailers
            }
            sortBy={filters.sortBy || DEFAULT_FILTER_VALUES.sortBy}
            onUpdate={newFilterValues => setValues(newFilterValues)}
            onMoreFiltersClick={() => setShowFiltersPanel(true)}
          />
        )}
        TableHeader={() => <CarrierTrailerHeader />}
        Row={({ element }) => (
          <CarrierTrailerRow trailer={element} showMissingTrailers={showMissingTrailers} />
        )}
        options={options}
        itemsPerPage={itemsPerPage}
        configQueryConfig={configQueryConfig}
        defaultFilterValues={DEFAULT_FILTER_VALUES}
        sortOptions={sortOptions}
        loading={loading}
        error={error}
        onFilterChange={filterChangeHandler}
        onPageChange={pageChangeHandler}
        showFilters={showFiltersPanel}
        onFilterHide={() => setShowFiltersPanel(false)}
        SidebarComponent={({ defaultValues, onClearAll, onSubmitError, onSubmitSuccess }) => (
          <CarrierTrailerFiltersForm
            defaultValues={defaultValues}
            onSubmitSuccess={onSubmitSuccess}
            onSubmitError={onSubmitError}
            onClearAll={onClearAll}
          />
        )}
        totalOptions={totalCount || 0}
        showTitle={false}
        title='Trailers'
      />
    </div>
  )
}

export default CarrierTrailers
